/**
 * Volume Profile Primitive
 *
 * Renders a volume profile histogram as horizontal bars overlaid on the price chart.
 * Features:
 * - Horizontal bars showing volume at each price level
 * - POC (Point of Control) highlighted in gold
 * - Value Area shading between VAH and VAL
 * - Delta coloring (green for positive, red for negative)
 */

import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import { VolumeProfileSnapshot, VolumeAtPrice } from '../../types/volumeProfile';

// ============================================================================
// Options Interface
// ============================================================================

export interface VolumeProfilePrimitiveOptions {
  /** Width of the profile as percentage of chart width (0-100) */
  widthPercent: number;
  /** Opacity of the bars (0-1) */
  opacity: number;
  /** Show on left or right side of chart */
  side: 'left' | 'right';
  /** Color for regular volume bars */
  volumeColor: string;
  /** Color for POC bar */
  pocColor: string;
  /** Color for value area shading */
  valueAreaColor: string;
  /** Color for VAH/VAL lines */
  vahValColor: string;
  /** Whether to show delta coloring */
  showDelta: boolean;
  /** Positive delta color */
  deltaPositiveColor: string;
  /** Negative delta color */
  deltaNegativeColor: string;
  /** Minimum bar height in pixels */
  minBarHeight: number;
  /** Whether to show value area (shading + VAH/VAL lines) */
  showValueArea: boolean;
  /** Whether to show VAH/VAL lines (only applies when showValueArea is true) */
  showVahVal: boolean;
}

// Default options
const DEFAULT_OPTIONS: VolumeProfilePrimitiveOptions = {
  widthPercent: 15,
  opacity: 0.6,
  side: 'right',
  volumeColor: '#2962FF',
  pocColor: '#FFD700',
  valueAreaColor: 'rgba(255, 215, 0, 0.08)',
  vahValColor: '#00BCD4', // Cyan for VAH/VAL lines
  showDelta: true,
  deltaPositiveColor: '#26A69A',
  deltaNegativeColor: '#EF5350',
  minBarHeight: 1,
  showValueArea: true,
  showVahVal: true,
};

// ============================================================================
// Renderer Data
// ============================================================================

interface VolumeProfileRendererData {
  /** Price level */
  price: number;
  /** Y coordinate on chart */
  y: number;
  /** Bar width (scaled by volume) */
  barWidth: number;
  /** Whether this is the POC level */
  isPoc: boolean;
  /** Whether this is within the Value Area */
  inValueArea: boolean;
  /** Delta value for coloring */
  delta: number;
}

interface ValueAreaBounds {
  topY: number;    // VAH Y coordinate
  bottomY: number; // VAL Y coordinate
  x: number;
  width: number;
  vahPrice: number;
  valPrice: number;
}

// ============================================================================
// Renderer
// ============================================================================

class VolumeProfilePaneRenderer implements IPrimitivePaneRenderer {
  private _data: VolumeProfileRendererData[] = [];
  private _valueAreaBounds: ValueAreaBounds | null = null;
  private _options: VolumeProfilePrimitiveOptions = DEFAULT_OPTIONS;

  update(
    data: VolumeProfileRendererData[],
    valueAreaBounds: ValueAreaBounds | null,
    options: VolumeProfilePrimitiveOptions
  ): void {
    this._data = data;
    this._valueAreaBounds = valueAreaBounds;
    this._options = options;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const verticalPixelRatio = scope.verticalPixelRatio as number;
      const bitmapWidth = scope.bitmapSize.width as number;

      // Draw value area background and VAH/VAL lines (behind bars)
      if (this._valueAreaBounds && this._options.showValueArea) {
        const va = this._valueAreaBounds;
        ctx.fillStyle = this._options.valueAreaColor;
        ctx.fillRect(
          va.x * pixelRatio,
          va.topY * verticalPixelRatio,
          va.width * pixelRatio,
          (va.bottomY - va.topY) * verticalPixelRatio
        );

        // Draw VAH and VAL lines if enabled
        if (this._options.showVahVal) {
          ctx.strokeStyle = this._options.vahValColor;
          ctx.lineWidth = 1 * pixelRatio;
          ctx.setLineDash([4 * pixelRatio, 4 * pixelRatio]); // Dashed line

          // VAH line (top of value area)
          ctx.beginPath();
          ctx.moveTo(0, va.topY * verticalPixelRatio);
          ctx.lineTo(bitmapWidth, va.topY * verticalPixelRatio);
          ctx.stroke();

          // VAL line (bottom of value area)
          ctx.beginPath();
          ctx.moveTo(0, va.bottomY * verticalPixelRatio);
          ctx.lineTo(bitmapWidth, va.bottomY * verticalPixelRatio);
          ctx.stroke();

          ctx.setLineDash([]); // Reset dash

          // Draw VAH/VAL labels with prices
          const fontSize = Math.round(9 * pixelRatio);
          ctx.font = `bold ${fontSize}px sans-serif`;
          ctx.textBaseline = 'middle';
          const labelX = 5 * pixelRatio; // Left margin

          // VAH label with price
          ctx.fillStyle = this._options.vahValColor;
          ctx.fillText(`VAH ${va.vahPrice.toFixed(2)}`, labelX, va.topY * verticalPixelRatio);

          // VAL label with price
          ctx.fillText(`VAL ${va.valPrice.toFixed(2)}`, labelX, va.bottomY * verticalPixelRatio);
        }
      }

      // Draw each volume bar
      for (const level of this._data) {
        const y = level.y * verticalPixelRatio;
        const barHeight = Math.max(this._options.minBarHeight * verticalPixelRatio, 2);

        // Calculate bar position based on side
        let barX: number;
        const scaledBarWidth = level.barWidth * pixelRatio;

        if (this._options.side === 'right') {
          barX = bitmapWidth - scaledBarWidth;
        } else {
          barX = 0;
        }

        // Choose color based on POC or delta
        let color: string;
        if (level.isPoc) {
          color = this._options.pocColor;
        } else if (this._options.showDelta) {
          color = level.delta >= 0
            ? this._options.deltaPositiveColor
            : this._options.deltaNegativeColor;
        } else {
          color = this._options.volumeColor;
        }

        // Draw bar with opacity
        ctx.globalAlpha = this._options.opacity;
        ctx.fillStyle = color;
        ctx.fillRect(barX, y - barHeight / 2, scaledBarWidth, barHeight);

        // Draw POC with full opacity and thicker bar + horizontal line + label
        if (level.isPoc) {
          ctx.globalAlpha = 1;
          ctx.fillStyle = this._options.pocColor;
          const pocHeight = Math.max(barHeight * 1.5, 3 * verticalPixelRatio);
          ctx.fillRect(barX, y - pocHeight / 2, scaledBarWidth, pocHeight);

          // Draw POC horizontal line across chart
          ctx.strokeStyle = this._options.pocColor;
          ctx.lineWidth = 1 * pixelRatio;
          ctx.setLineDash([]); // Solid line for POC
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(bitmapWidth, y);
          ctx.stroke();

          // Draw POC label with price
          const pocFontSize = Math.round(9 * pixelRatio);
          ctx.font = `bold ${pocFontSize}px sans-serif`;
          ctx.textBaseline = 'middle';
          ctx.fillStyle = this._options.pocColor;
          ctx.fillText(`POC ${level.price.toFixed(2)}`, 5 * pixelRatio, y);
        }
      }

      // Reset alpha
      ctx.globalAlpha = 1;
    });
  }
}

// ============================================================================
// Pane View
// ============================================================================

class VolumeProfilePaneView implements IPrimitivePaneView {
  private _source: VolumeProfilePrimitive;
  private _renderer: VolumeProfilePaneRenderer;

  constructor(source: VolumeProfilePrimitive) {
    this._source = source;
    this._renderer = new VolumeProfilePaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'bottom'; // Draw behind candles
  }

  renderer(): IPrimitivePaneRenderer {
    const { data, valueAreaBounds } = this._source.getRendererData();
    this._renderer.update(data, valueAreaBounds, this._source.getOptions());
    return this._renderer;
  }
}

// ============================================================================
// Primitive
// ============================================================================

export class VolumeProfilePrimitive implements ISeriesPrimitive<Time> {
  private _options: VolumeProfilePrimitiveOptions;
  private _data: VolumeProfileSnapshot | null = null;
  private _paneView: VolumeProfilePaneView;
  private _requestUpdate?: () => void;
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _enabled: boolean = true;

  constructor(options: Partial<VolumeProfilePrimitiveOptions> = {}) {
    this._options = { ...DEFAULT_OPTIONS, ...options };
    this._paneView = new VolumeProfilePaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._chart = undefined;
    this._series = undefined;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  /**
   * Update the volume profile data
   */
  updateData(snapshot: VolumeProfileSnapshot): void {
    this._data = snapshot;
    this._requestUpdate?.();
  }

  /**
   * Update primitive options
   */
  updateOptions(options: Partial<VolumeProfilePrimitiveOptions>): void {
    this._options = { ...this._options, ...options };
    this._requestUpdate?.();
  }

  /**
   * Get current options
   */
  getOptions(): VolumeProfilePrimitiveOptions {
    return this._options;
  }

  /**
   * Enable/disable the primitive
   */
  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    this._requestUpdate?.();
  }

  /**
   * Check if primitive is enabled
   */
  isEnabled(): boolean {
    return this._enabled;
  }

  /**
   * Clear all data
   */
  clearData(): void {
    this._data = null;
    this._requestUpdate?.();
  }

  /**
   * Get renderer data with calculated coordinates
   */
  getRendererData(): {
    data: VolumeProfileRendererData[];
    valueAreaBounds: ValueAreaBounds | null;
  } {
    if (!this._chart || !this._series || !this._data || !this._enabled) {
      return { data: [], valueAreaBounds: null };
    }

    const { levels, pocPrice, valueAreaHigh, valueAreaLow } = this._data;

    if (levels.length === 0) {
      return { data: [], valueAreaBounds: null };
    }

    // Calculate max volume for scaling
    const maxVolume = Math.max(...levels.map(l => l.totalVolume));
    if (maxVolume === 0) {
      return { data: [], valueAreaBounds: null };
    }

    // Get chart dimensions
    const chartWidth = this._chart.timeScale().width();
    const profileWidth = (chartWidth * this._options.widthPercent) / 100;

    // Calculate value area bounds
    let valueAreaBounds: ValueAreaBounds | null = null;
    const vaHighY = this._series.priceToCoordinate(valueAreaHigh);
    const vaLowY = this._series.priceToCoordinate(valueAreaLow);

    if (vaHighY !== null && vaLowY !== null) {
      valueAreaBounds = {
        topY: vaHighY,
        bottomY: vaLowY,
        x: this._options.side === 'right' ? chartWidth - profileWidth : 0,
        width: profileWidth,
        vahPrice: valueAreaHigh,
        valPrice: valueAreaLow,
      };
    }

    // Build renderer data for each level
    const data: VolumeProfileRendererData[] = [];

    for (const level of levels) {
      const y = this._series.priceToCoordinate(level.price);
      if (y === null) continue;

      // Scale bar width by volume
      const barWidth = (level.totalVolume / maxVolume) * profileWidth;

      // Check if this is POC (within small epsilon for floating point comparison)
      const isPoc = Math.abs(level.price - pocPrice) < 0.001;

      // Check if within value area
      const inValueArea = level.price >= valueAreaLow && level.price <= valueAreaHigh;

      data.push({
        price: level.price,
        y,
        barWidth,
        isPoc,
        inValueArea,
        delta: level.delta,
      });
    }

    return { data, valueAreaBounds };
  }
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create a new VolumeProfilePrimitive instance
 */
export function createVolumeProfilePrimitive(
  options?: Partial<VolumeProfilePrimitiveOptions>
): VolumeProfilePrimitive {
  return new VolumeProfilePrimitive(options);
}
